import React, { useState } from 'react';
import { Clock, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

export function CostCalculator() {
  const [contentCount, setContentCount] = useState(5);
  const [hourlyRate, setHourlyRate] = useState(100);
  
  // Time calculations
  const timePerPost = 77; // in minutes
  const totalTime = contentCount * timePerPost; // in minutes
  const timeWithTool = contentCount * 10; // in minutes (assumes 10 min per post with our tool)
  const timeSaved = totalTime - timeWithTool; // in minutes
  
  // Secondary money calculations
  const totalTimeCost = (totalTime / 60) * hourlyRate;
  const toolTimeCost = (timeWithTool / 60) * hourlyRate;
  const moneySaved = totalTimeCost - toolTimeCost;
  
  // Formatted values for display
  const formattedTotalTime = `${Math.floor(totalTime / 60)}h ${totalTime % 60}m`;
  const formattedTimeSaved = `${Math.floor(timeSaved / 60)}h ${timeSaved % 60}m`;
  const formattedMoneySaved = `$${moneySaved.toFixed(0)}`;

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-black text-white rounded-2xl shadow-xl p-6 md:p-8">
            <h2 className="text-2xl font-bold mb-6 text-center flex items-center justify-center gap-2">
              <Clock className="w-6 h-6" />
              How Much Time Are You Wasting on Content?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="space-y-6">
                <div className="relative">
                  <label className="block text-sm font-medium mb-2 text-white">Monthly Content Pieces</label>
                  <input
                    type="range"
                    min="1"
                    max="20"
                    value={contentCount}
                    onChange={(e) => setContentCount(parseInt(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
                  />
                  <div className="flex justify-between text-sm mt-1 text-gray-100">
                    <span>1</span>
                    <span>5</span>
                    <span>10</span>
                    <span>15</span>
                    <span>20</span>
                  </div>
                  <div className="text-center mt-2 font-bold text-2xl">{contentCount} posts</div>
                </div>
                
                <div className="relative">
                  <label className="block text-sm font-medium mb-2 text-white">Your Hourly Value</label>
                  <input
                    type="range"
                    min="25"
                    max="250"
                    step="25"
                    value={hourlyRate}
                    onChange={(e) => setHourlyRate(parseInt(e.target.value))}
                    className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
                  />
                  <div className="flex justify-between text-sm mt-1 text-gray-100">
                    <span>$25</span>
                    <span>$100</span>
                    <span>$250</span>
                  </div>
                  <div className="text-center mt-2 font-bold text-2xl">${hourlyRate}/hr</div>
                </div>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 text-white">
                <h3 className="text-xl font-bold mb-4 text-center">Your Time Savings</h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <Clock className="w-5 h-5 mr-2 text-yellow-300" />
                      <span>Current Time Spent</span>
                    </div>
                    <span className="font-semibold">{formattedTotalTime}</span>
                  </div>
                  
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-green-300" />
                      <span>Time With Our Tool</span>
                    </div>
                    <span className="font-semibold">{Math.floor(timeWithTool / 60)}h {timeWithTool % 60}m</span>
                  </div>
                  
                  <div className="h-px bg-gray-600 my-2"></div>
                  
                  <div className="flex justify-between items-center text-xl font-bold">
                    <span>Time Saved:</span>
                    <span className="text-yellow-300">{formattedTimeSaved}</span>
                  </div>

                  <div className="flex justify-between items-center text-base">
                    <span>Money Saved:</span>
                    <span className="text-green-300">{formattedMoneySaved}</span>
                  </div>

                  <p className="mt-2 text-sm text-white">
                    Imagine what you could do with an extra <span className="font-bold text-yellow-300">{Math.floor(timeSaved / 60)}</span> hours each month instead of wrestling with AI prompts.
                  </p>
                </div>
                
                <div className="mt-6 text-center">
                  <Link to="/upgrade">
                    <Button className="bg-dogbrown hover:bg-dogbrown/90 text-white font-medium">
                      Get Your Time Back Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}